/**
 * Survey Form JKL — submits loyalty survey answers to the JConnect endpoint.
 *
 * Form discovery: any <form data-survey-form> in the DOM.
 * Endpoint & metadata come from data-* attributes on the form, so JS logic
 * is decoupled from the survey (any survey works with the same script).
 *
 * Wire contract (must match the JConnect submit-survey endpoint):
 *   POST /apps/caffeborbone/shopify/loyalty/submit-survey  (via Shopify App Proxy)
 *   Body JSON: {
 *     customer_id: string,   // numeric ID from Liquid ({{ customer.id }})
 *     survey_id:   string,   // human-readable, e.g. "Questionario di Benvenuto"
 *     answers:     object,   // { domanda_1: "A", domanda_2: "B", ... }
 *     language:    string    // "it" | "en" | ... (from <html lang="">)
 *   }
 *
 * Response shape:
 *   Success: 200 { success: true }
 *   Error:   4xx/5xx { success: false, message, localizedMessage }
 */
(function () {
  "use strict";

  // Form selector — all data-survey-form elements are wired up.
  const FORM_SELECTOR = "form[data-survey-form]";

  // Shared secret — validated by JConnect's WebhookMiddleware against the first
  // (secret.length - 10) characters of CAFFE_BORBONE_SHOPIFY_WEBHOOK_SECRET.
  // Same value used by assets/loyalty-jkl.js — see that file for the pattern.
  const X_API_SECRET = "0ddd235d06f03f3dde1156f7ecb46dc978af42d4b409300f7bc618";

  // Abort the fetch after this many ms so a stuck request doesn't leave the
  // submit button disabled forever.
  const REQUEST_TIMEOUT_MS = 30000;

  // Ultimate fallback messages (Italian) — used only if the form has no
  // data-fallback-* attributes for some reason. In practice these strings
  // come from locale files via Liquid: see survey-form-jkl.liquid.
  //
  // Rationale: JS cannot read Shopify locale files directly. So the
  // section injects the translated strings into data-* attributes of
  // the <form>, and each helper below reads them per-form. That way
  // a single script serves every language the store supports.
  const HARDCODED_FALLBACK_ERROR =
    "Si è verificato un errore. Riprova più tardi.";
  const HARDCODED_FALLBACK_VALIDATION =
    "Compila tutte le domande prima di inviare.";

  /**
   * Reads a localized fallback message from the form's data-* attribute,
   * falling back to the Italian hardcoded string if missing.
   * key is either "error" or "validation".
   */
  function fallbackMessage(form, key) {
    if (key === "validation") {
      return form.dataset.fallbackValidation || HARDCODED_FALLBACK_VALIDATION;
    }
    return form.dataset.fallbackError || HARDCODED_FALLBACK_ERROR;
  }

  /**
   * Reads the current document language from <html lang="..."> and returns
   * the first 2 chars lowercased. Defaults to "it".
   */
  function detectLanguage() {
    const raw = document.documentElement.lang || "it";
    return raw.slice(0, 2).toLowerCase();
  }

  /**
   * Collects form answers into an object keyed by question_id.
   *
   * For radio inputs → single string ("A", "B", ...).
   * For checkbox inputs (name ending with "[]") → comma-separated string
   * ("A,C,E") so it fits the metaobject's Single line text field.
   *
   * Empty questions (no answer selected) are OMITTED from the payload.
   * The server-side validation will reject the submit if required answers
   * are missing — but we also block submit client-side via form.checkValidity.
   */
  function collectAnswers(form) {
    const data = new FormData(form);
    const answers = {};

    for (const [rawKey, value] of data.entries()) {
      // Strip "[]" suffix from checkbox names so the payload key stays clean.
      const key = rawKey.endsWith("[]") ? rawKey.slice(0, -2) : rawKey;

      if (answers[key] === undefined) {
        answers[key] = String(value);
      } else {
        // Second (or later) value for the same key → this was a checkbox
        // group. Concatenate as comma-separated string.
        answers[key] = answers[key] + "," + String(value);
      }
    }

    return answers;
  }

  /**
   * Builds the JSON body sent to the JConnect endpoint.
   * Reads survey_id, customer_id from data-* attributes so multiple surveys
   * can share the same script without hard-coding IDs.
   */
  function buildPayload(form) {
    return {
      customer_id: form.dataset.customerId || "",
      survey_id: form.dataset.surveyId || "",
      answers: collectAnswers(form),
      language: detectLanguage(),
    };
  }

  /**
   * Shows a message in the banner element associated with the form.
   * status is "error" | "success" and controls the CSS variant.
   */
  function showBanner(banner, message, status) {
    if (!banner) return;
    banner.textContent = message || "";
    banner.hidden = !message;
    banner.classList.remove(
      "survey-jkl__banner--success",
      "survey-jkl__banner--error",
    );
    if (message && status) {
      banner.classList.add("survey-jkl__banner--" + status);
    }
  }

  function hideBanner(banner) {
    if (!banner) return;
    banner.hidden = true;
    banner.textContent = "";
    banner.classList.remove(
      "survey-jkl__banner--success",
      "survey-jkl__banner--error",
    );
  }

  /**
   * Extracts a user-friendly error message from a fetch Response.
   * Prefers localizedMessage → message → fallback, in that order.
   */
  function readErrorMessage(response, fallback) {
    const contentType = (
      response.headers.get("Content-Type") || ""
    ).toLowerCase();
    if (contentType.indexOf("application/json") === -1) {
      return Promise.resolve(fallback);
    }
    return response.json().then(
      function (data) {
        const msg = data && (data.localizedMessage || data.message);
        return msg ? String(msg) : fallback;
      },
      function () {
        return fallback;
      },
    );
  }

  /**
   * Re-enables the submit button (in error paths, or when validation blocks
   * the submit). Success path leaves it disabled and redirects instead.
   */
  function resetSubmitButton(button) {
    if (!button) return;
    button.disabled = false;
    button.removeAttribute("aria-busy");
  }

  /**
   * The main submit handler.
   * Steps:
   *   1. Client validation via checkValidity.
   *   2. Disable button, show busy state.
   *   3. Fire fetch with a timeout controller.
   *   4. On 200 → redirect to thank-you page.
   *   5. On non-200 → show error banner and re-enable button.
   *   6. On network error / timeout → same as (5) with fallback message.
   */
  function submitForm(form, banner) {
    const submitButton = form.querySelector('button[type="submit"]');

    hideBanner(banner);

    if (!form.checkValidity()) {
      form.reportValidity();
      showBanner(banner, fallbackMessage(form, "validation"), "error");
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute("aria-busy", "true");
    }

    const endpoint =
      form.dataset.endpoint ||
      "/apps/caffeborbone/shopify/loyalty/submit-survey";
    const thankYouUrl = form.dataset.thankYouUrl || "/pages/grazie-survey";

    // Guard against fetch hangs — abort after REQUEST_TIMEOUT_MS.
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(function () {
          controller.abort();
        }, REQUEST_TIMEOUT_MS)
      : null;

    // Build payload once so we can also log it for debugging.
    const payload = buildPayload(form);

    // Debug log — helps identify empty customer_id, missing answers, or
    // wrong endpoint when the submit does not behave as expected.
    // Safe to leave in production: only prints to the developer console.
    console.log("[survey-form] POST", endpoint, payload);

    const fetchOptions = {
      method: "POST",
      // Same-origin credentials so the Incapsula session cookie (set on
      // page load) is forwarded with the POST. Without it the WAF sees a
      // "fresh" client and serves a JS challenge page.
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Some WAFs (Incapsula) treat XHR/fetch without this header as
        // suspicious. Adding it does not break anything backend-side.
        "X-Requested-With": "XMLHttpRequest",
        "X-Api-Secret": X_API_SECRET,
      },
      body: JSON.stringify(payload),
    };
    if (controller) fetchOptions.signal = controller.signal;

    fetch(endpoint, fetchOptions)
      .then(function (response) {
        if (timeoutId) clearTimeout(timeoutId);

        const contentType = (
          response.headers.get("Content-Type") || ""
        ).toLowerCase();

        // Debug log — print status + content-type so we can see if the
        // proxy returned HTML (e.g. login page redirect or WAF challenge)
        // instead of JSON.
        console.log("[survey-form] response", response.status, contentType);

        // JConnect always answers with application/json. If the response
        // is HTML we know the request never reached the backend — most
        // common cause is a WAF (Incapsula) challenge that swallows the
        // App Proxy POST. Treat it as an error even if the HTTP status
        // says 200, so we don't falsely redirect to the thank-you page.
        const isJsonResponse = contentType.indexOf("application/json") !== -1;

        if (response.ok && isJsonResponse) {
          // 200 + JSON → server saved the metaobject + set the completion
          // metafield. Redirect to the thank-you page; the button stays
          // disabled to avoid double-submit if the redirect is slow.
          window.location.href = thankYouUrl;
          return null;
        }

        // Clone the response so we can log the raw body without consuming
        // the stream used by readErrorMessage below.
        response
          .clone()
          .text()
          .then(function (text) {
            console.log("[survey-form] error body", text);
          })
          .catch(function () {});

        return readErrorMessage(response, fallbackMessage(form, "error")).then(
          function (message) {
            showBanner(banner, message, "error");
            resetSubmitButton(submitButton);
          },
        );
      })
      .catch(function (err) {
        if (timeoutId) clearTimeout(timeoutId);
        console.log("[survey-form] network error", err);
        showBanner(banner, fallbackMessage(form, "error"), "error");
        resetSubmitButton(submitButton);
      });
  }

  /**
   * Wire a single form's submit event.
   */
  function initForm(form) {
    const bannerId = form.getAttribute("data-banner-id");
    const banner = bannerId ? document.getElementById(bannerId) : null;

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      submitForm(form, banner);
    });
  }

  /**
   * Entry point — wire every survey form found on the page.
   */
  function init() {
    document.querySelectorAll(FORM_SELECTOR).forEach(initForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
